import { useState } from "react";
import {
  useDeleteAdminMutation,
  useFetchAdminsQuery,
  useToggleAdminIsActiveStatusMutation,
} from "../store";
import Pagination from "../components/common/Pagination.tsx";
import ToggleSwitch from "../components/common/ToggleSwitch.tsx";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn.tsx";
import { tdClasses, thClasses } from "../configs";
import { Admin } from "../types";
import { showToast, ToastType } from "../utils/toast.utils.ts";
import { FaExternalLinkAlt, FaRegTrashAlt } from "react-icons/fa";
import Modal from "../components/common/Modal.tsx";
import Button from "../components/common/Button.tsx";
import useDebounce from "../hooks/useDebounce.tsx";
import TextField from "../components/common/TextField.tsx";
import { Link } from "react-router-dom";

const Admins = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [name, setName] = useState<string>("");
  const debouncedName = useDebounce(name, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null);

  const [deleteAdmin] = useDeleteAdminMutation();

  const confirmDelete = (adminToDelete: Admin) => {
    setAdminToDelete(adminToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;
    const result = await deleteAdmin(adminToDelete._id);
    if (!result.error) {
      showToast("Administrateur supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setAdminToDelete(null);
  };

  const [toggleIsActive] = useToggleAdminIsActiveStatusMutation();

  const { data: adminsInfo } = useFetchAdminsQuery({
    page,
    perPage,
    name: debouncedName,
  });

  return (
    <div className="w-full flex flex-col p-4">
      <div className="flex items-center justify-between">
        <div className="w-1/4">
          <TextField
            id="name"
            value={name}
            onChange={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="Nom ou prénom de l'administrateur"
          />
        </div>
        <NavigationLinkBtn
          to="/admins/create"
          label="Créer un admin"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-3/12" />
          <col className="w-3/12" />
          <col className="w-3/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th
              key={0}
              className={thClasses}
            >
              Nom
            </th>
            <th
              key={1}
              className={thClasses}
            >
              Prénom
            </th>
            <th
              key={2}
              className={thClasses}
            >
              email
            </th>
            <th
              key={3}
              className={thClasses}
            >
              Actif
            </th>
            <th
              key={4}
              className={thClasses}
            >
              Mise à jour
            </th>
            <th
              key={5}
              className={thClasses}
            >
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {adminsInfo?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.firstName} </td>
                <td className={tdClasses}> {row.lastName} </td>
                <td className={tdClasses}> {row.email} </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <ToggleSwitch
                      isOn={row.isActive}
                      onToggle={() => {
                        toggleIsActive({
                          adminId: row._id,
                          isActive: !row.isActive,
                        });
                      }}
                    />
                  </div>
                </td>
                <td className={tdClasses}>
                  <Link
                    className="w-full flex justify-center"
                    to={row._id}
                  >
                    <FaExternalLinkAlt className="w-8 text-primary" />
                  </Link>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: adminsInfo?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteAdmin}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer{" "}
            {`${adminToDelete?.firstName} ${adminToDelete?.lastName}`} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Admins;
